/**
 * @author: lv.chen
 * @date: 2025/12/30
 * @desc: ProductChangedRankMiniGame组件 - 小游戏异动榜公共组件
 */
import React from 'react';
import classnames from 'classnames';
import EllipsisHover from '@home/components/EllipsisHover';
import GameImage from '@home/components/GameImage';
import RankChange from '../RankChange';
import { isEmpty } from '@intelligence/v3/utils/common';
import { NEW_TOP, getRankElement, renderChangeTag } from '../../utils';

import IMG_RANK_BLACK from '@intelligence/assets/imgs/casualDailyReports/icon-rank-black.png';

/** 默认显示数量 */
const DISPLAY_COUNT = 3;
/** 展开后显示数量 */
const EXPANDED_COUNT = 10;

interface IProductChangedRankMiniGameProps extends IBaseProps {
  /** 标题文字 */
  title: string;
  /** 第二榜单标题，默认"畅玩榜" */
  secondaryTitle?: string;
  /** 平台图标 */
  platformIcon: string;
  /** 第一榜单标题 */
  firstRankTitle: string;
  /** 第一榜单样式class */
  firstRankClass?: string;
  /** 第二榜单标题 */
  secondRankTitle: string;
  /** 第二榜单样式class */
  secondRankClass?: string;
  /** 人气榜数据 */
  popularityRanks?: IMiniGameRankItem[] | null;
  /** 第二榜单数据(畅玩榜/新游榜) */
  secondaryRanks?: IMiniGameRankItem[] | null;
  /** 是否展开显示Top10 */
  isExpanded?: boolean;
  /** 展开/收起回调 */
  onToggleExpand?: () => void;
}

const ProductChangedRankMiniGame: React.FC<IProductChangedRankMiniGameProps> = (props) => {
  const {
    title,
    platformIcon,
    firstRankTitle,
    firstRankClass,
    secondRankTitle,
    secondRankClass,
    popularityRanks,
    secondaryRanks,
    isExpanded = false,
    onToggleExpand,
  } = props;

  // 根据展开状态决定显示数量
  const displayCount = isExpanded ? EXPANDED_COUNT : DISPLAY_COUNT;
  const popularityDisplayRanks = popularityRanks?.slice(0, displayCount) || [];
  const secondaryDisplayRanks = secondaryRanks?.slice(0, displayCount) || [];
  const hasMoreData = (popularityRanks?.length || 0) > DISPLAY_COUNT || (secondaryRanks?.length || 0) > DISPLAY_COUNT;

  // 渲染排名变化
  const renderRankChange = (item: IMiniGameRankItem) => {
    const { game_rank: gameRank, rank_change: rankChange, tag, tag_desc: tagDesc } = item || {};

    return (
      <>
        {renderChangeTag(tag, tagDesc)}
        <span className="s1">{gameRank}</span>
        <RankChange className={classnames({ 'dbi--newtop': rankChange === NEW_TOP })} data={rankChange} />
      </>
    );
  };

  // 渲染榜单项
  const renderRankItem = (item: IMiniGameRankItem, index: number) => {
    const { app_name: appName, app_cover: appCover, publisher } = item || {};
    return (
      <div key={appName} className="dbi-update-rank-item">
        <div className="dbi-rank-card">
          <div className="dbi-rank-card-index">{getRankElement(index)}</div>
          <div className="dbi-rank-card-info">
            <div className="dbi-rank-card-info-avatar">
              <GameImage entityType="mobile" src={appCover} />
            </div>
            <div className="dbi-rank-card-info-text">
              <div className="dbi-rank-card-info-title">{appName}</div>
              <div className="dbi-rank-card-info-desc">
                <EllipsisHover text={publisher} />
              </div>
            </div>
          </div>
        </div>
        <div className="dbi-update-rank-card-td">
          <div className="dbi-rank-card-info-trend">
            <div className="dbi-rank-info-trend-percent">{renderRankChange(item)}</div>
          </div>
        </div>
      </div>
    );
  };

  // 空状态渲染
  const renderEmptyState = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '162px',
      }}
    >
      <span
        style={{
          fontWeight: 400,
          fontSize: '14px',
          color: '#838697',
        }}
      >
        当前榜单跟昨天完全一致，无异动游戏
      </span>
    </div>
  );

  // 渲染单个子榜单
  const renderSubRank = (
    rankTitle: string,
    rankClass: string | undefined,
    ranks: IMiniGameRankItem[] | null | undefined,
  ) => (
    <>
      <div className={classnames('section-rank-bill-title', rankClass)}>{rankTitle}</div>
      {!isEmpty(ranks) ? ranks?.map((item, index) => renderRankItem(item, index)) : renderEmptyState()}
    </>
  );

  return (
    <div className="dbi-update-rank-card dbi-update-rank-card--app">
      <div className="dbi-update-rank-card-content">
        <div className="dbi-update-rank-card-hd">
          <div className="dbi-update-rank-card-title">
            <img className="dbi-update-rank-card-title-img" src={platformIcon} alt="" />
            <span>{title}</span>
          </div>
          <div className="dbi-update-rank-card-th">
            排名/变化量
            <img src={IMG_RANK_BLACK} className="dbi-icon-rank-down" alt="" />
          </div>
        </div>
        <div className="dbi-update-rank-list">
          {renderSubRank(firstRankTitle, firstRankClass, popularityDisplayRanks)}
          {renderSubRank(secondRankTitle, secondRankClass, secondaryDisplayRanks)}
        </div>
        {/* TODO: @高靖 靖哥这里是展开、折叠的按钮，这里需要小心点，不要把状态、事件改丢拉 */}
        {/* 展开收起按钮 */}
        {hasMoreData && onToggleExpand && (
          <div
            className={classnames('dbi-mini-game-expand-btn', {
              'dbi-mini-game-expand-btn--expanded': isExpanded,
            })}
            onClick={onToggleExpand}
          >
            <span className="dbi-mini-game-expand-text">
              <span className="dbi-mini-game-expand-text-s">{isExpanded ? '收起' : '展开'}</span>
              <span
                className={classnames('dbi-mini-game-expand-icon', {
                  'dbi-mini-game-expand-icon--up': isExpanded,
                })}
              />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductChangedRankMiniGame;
